import { useEffect, useMemo, useState, Fragment } from "react";
import { api, formatErr } from "../api";
import StaffLayout from "../StaffLayout";
import { Trash2, Check, X, ChevronRight, Download, FileText, Pencil } from "lucide-react";

const inputCls =
  "bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark transition-colors text-ink text-sm";

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Admin() {
  const [tab, setTab] = useState("team");
  return (
    <StaffLayout>
      <div className="mb-10">
        <p className="overline text-matcha-dark">— Admin</p>
        <h1 className="font-display text-ink mt-2" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", letterSpacing: "0.04em" }}>
          Team Overview
        </h1>
      </div>
      <div className="flex items-center gap-6 border-b border-ink/15 mb-8">
        {[
          { k: "team", label: "Team" },
          { k: "attendance", label: "Attendance" },
          { k: "payroll", label: "Payroll" },
          { k: "holidays", label: "Holidays" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`admin-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`pb-3 -mb-px overline transition-colors ${
              tab === t.k ? "text-matcha-dark border-b-2 border-matcha-dark" : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "team" && <TeamTab />}
      {tab === "attendance" && <AttendanceTab />}
      {tab === "payroll" && <PayrollTab />}
      {tab === "holidays" && <HolidaysTab />}
    </StaffLayout>
  );
}

function TeamTab() {
  const [staff, setStaff] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // staff id being edited

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/staff");
      setStaff(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleApprove = async (s) => {
    await api.patch(`/staff/${s.id}/approve`, { active: !s.active });
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.full_name}?`)) return;
    await api.delete(`/staff/${s.id}`);
    load();
  };

  return (
    <div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
      <div className="bg-bone border border-ink/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-4 py-3 overline">Name</th>
              <th className="px-4 py-3 overline">Role</th>
              <th className="px-4 py-3 overline">Status</th>
              <th className="px-4 py-3 overline">Employment</th>
              <th className="px-4 py-3 overline">Rate / Salary</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-ink/5 align-top" data-testid={`team-row-${s.id}`}>
                <td className="px-4 py-3">
                  <p className="text-ink">{s.full_name}</p>
                  <p className="overline text-ink-soft mt-1">{s.email}</p>
                  <p className="text-xs text-ink-soft mt-1">{s.contact_no}</p>
                </td>
                <td className="px-4 py-3 text-ink-soft">{s.role}</td>
                <td className="px-4 py-3">
                  {s.role === "admin" ? (
                    <span className="overline text-matcha-dark">—</span>
                  ) : (
                    <button
                      data-testid={`approve-toggle-${s.id}`}
                      onClick={() => toggleApprove(s)}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 overline ${
                        s.active
                          ? "bg-matcha-dark text-bone"
                          : "bg-yellow-100 text-yellow-900 hover:bg-yellow-200"
                      }`}
                    >
                      {s.active ? <><Check size={12} /> Active</> : <><X size={12} /> Pending</>}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-ink-soft">
                  {s.employment_type === "full_time" && "Full-time"}
                  {s.employment_type === "part_time" && "Part-time"}
                  {!s.employment_type && s.role !== "admin" && "—"}
                </td>
                <td className="px-4 py-3 text-ink">
                  {s.employment_type === "full_time" && `RM ${s.basic_salary}/mo`}
                  {s.employment_type === "part_time" && `RM ${s.hourly_rate}/hr`}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {s.role !== "admin" && (
                    <>
                      <button
                        data-testid={`edit-emp-${s.id}`}
                        onClick={() => setEditing(s.id)}
                        className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3"
                      >
                        Set Employment
                      </button>
                      <button
                        data-testid={`delete-staff-${s.id}`}
                        onClick={() => remove(s)}
                        className="text-ink-soft hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {staff.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-ink-soft">No staff yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EmploymentModal
          staff={staff.find((s) => s.id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EmploymentModal({ staff, onClose, onSaved }) {
  const [type, setType] = useState(staff?.employment_type || "full_time");
  const [basic, setBasic] = useState(staff?.basic_salary || "");
  const [rate, setRate] = useState(staff?.hourly_rate || "");
  const [nhpd, setNhpd] = useState(staff?.normal_hours_per_day ?? "");
  const [otRate, setOtRate] = useState(staff?.ot_hourly_rate ?? "");
  const [dateJoined, setDateJoined] = useState(staff?.date_joined || (staff?.created_at ? String(staff.created_at).slice(0, 10) : ""));
  const [position, setPosition] = useState(staff?.position || "");
  const [bankName, setBankName] = useState(staff?.bank_name || "");
  const [bankAccount, setBankAccount] = useState(staff?.bank_account || "");
  const [pcbEligible, setPcbEligible] = useState(!!staff?.pcb_eligible);
  const [outlets, setOutlets] = useState([]);
  const [outletCodes, setOutletCodes] = useState(staff?.outlet_codes || []);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/inv/outlets").then((r) => setOutlets(r.data || [])).catch(() => setOutlets([]));
  }, []);

  const toggleOutlet = (code) => {
    setOutletCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  // Malaysia EA derived rates (readonly displays)
  const nhpdNum = Number(nhpd) || 8;
  const basicNum = Number(basic) || 0;
  const ordinaryRate = basicNum > 0 ? basicNum / 26 : 0;
  const hourlyRate = basicNum > 0 && nhpdNum > 0 ? ordinaryRate / nhpdNum : 0;

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.patch(`/staff/${staff.id}/employment`, {
        employment_type: type,
        basic_salary: type === "full_time" ? parseFloat(basic) : null,
        hourly_rate: type === "part_time" ? parseFloat(rate) : null,
        normal_hours_per_day: type === "full_time" && nhpd !== "" ? parseFloat(nhpd) : null,
        ot_hourly_rate: otRate === "" || otRate === null ? null : parseFloat(otRate),
        date_joined: dateJoined || null,
        position: position || null,
        bank_name: bankName || null,
        bank_account: bankAccount || null,
        pcb_eligible: pcbEligible,
      });
      await api.patch(`/staff/${staff.id}/outlets`, { outlet_codes: outletCodes });
      onSaved();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6" data-testid="employment-modal">
      <div className="bg-bone rounded-sm p-8 w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <h3 className="font-display text-xl text-ink mb-1" style={{ letterSpacing: "0.05em" }}>Set Employment</h3>
        <p className="overline text-ink-soft mb-6">{staff?.full_name}</p>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {["full_time", "part_time"].map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`emp-type-${v}`}
                onClick={() => setType(v)}
                className={`py-3 rounded-sm border text-xs uppercase tracking-[0.18em] transition-colors ${
                  type === v ? "bg-ink text-bone border-ink" : "border-ink/20 hover:border-ink/40"
                }`}
              >
                {v === "full_time" ? "Full-time" : "Part-time"}
              </button>
            ))}
          </div>
          {type === "full_time" ? (
            <div className="space-y-4">
              <p className="overline text-matcha-dark">Salary Details · Malaysia EA</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-ink-soft block mb-2">Monthly Salary (RM)</label>
                  <input data-testid="emp-basic" required type="number" min="0" step="0.01" className={`${inputCls} w-full`} value={basic} onChange={(e) => setBasic(e.target.value)} />
                </div>
                <div>
                  <label className="overline text-ink-soft block mb-2">Ordinary Rate of Pay (RM)</label>
                  <input
                    data-testid="emp-ordinary-rate"
                    readOnly
                    tabIndex={-1}
                    className={`${inputCls} w-full bg-cream/60 text-ink-soft cursor-not-allowed`}
                    value={ordinaryRate > 0 ? ordinaryRate.toFixed(2) : ""}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label className="overline text-ink-soft block mb-2">Normal Working Hours per Day</label>
                  <input
                    data-testid="emp-nhpd"
                    type="number"
                    min="1"
                    max="24"
                    step="0.5"
                    placeholder="8"
                    className={`${inputCls} w-full`}
                    value={nhpd}
                    onChange={(e) => setNhpd(e.target.value)}
                  />
                </div>
                <div>
                  <label className="overline text-ink-soft block mb-2">Hourly Rate of Pay (RM)</label>
                  <input
                    data-testid="emp-hourly-rate"
                    readOnly
                    tabIndex={-1}
                    className={`${inputCls} w-full bg-cream/60 text-ink-soft cursor-not-allowed`}
                    value={hourlyRate > 0 ? hourlyRate.toFixed(2) : ""}
                    placeholder="—"
                  />
                </div>
              </div>
              <p className="text-[10px] text-ink-soft">
                Ordinary Rate = Monthly / 26 · Hourly Rate = Ordinary / Normal Hours per Day. Weekday OT is auto-paid at Hourly Rate × 1.5.
              </p>
            </div>
          ) : (
            <div>
              <label className="overline text-ink-soft block mb-2">Hourly rate (RM / hour)</label>
              <input data-testid="emp-rate" required type="number" min="0" step="0.01" className={`${inputCls} w-full`} value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
          )}
          <div>
            <label className="overline text-ink-soft block mb-2">OT hourly rate override (RM / hour)</label>
            <input
              data-testid="emp-ot-rate"
              type="number"
              min="0"
              step="0.01"
              placeholder={type === "full_time" && hourlyRate > 0 ? `${hourlyRate.toFixed(2)} (auto)` : "auto"}
              className={`${inputCls} w-full`}
              value={otRate}
              onChange={(e) => setOtRate(e.target.value)}
            />
            <p className="text-[10px] text-ink-soft mt-1">Leave blank to auto-use the Hourly Rate of Pay above.</p>
          </div>
          <div className="pt-2 border-t border-ink/10 space-y-4">
            <p className="overline text-matcha-dark">Payslip Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-ink-soft block mb-2">Date Joined</label>
                <input
                  data-testid="emp-date-joined"
                  type="date"
                  className={`${inputCls} w-full`}
                  value={dateJoined}
                  onChange={(e) => setDateJoined(e.target.value)}
                />
              </div>
              <div>
                <label className="overline text-ink-soft block mb-2">Position</label>
                <input
                  data-testid="emp-position"
                  type="text"
                  placeholder="e.g. OPERATION STAFF"
                  className={`${inputCls} w-full`}
                  value={position}
                  onChange={(e) => setPosition(e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className="overline text-ink-soft block mb-2">Bank name</label>
                <input
                  data-testid="emp-bank-name"
                  type="text"
                  placeholder="e.g. MAYBANK"
                  className={`${inputCls} w-full`}
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className="overline text-ink-soft block mb-2">Bank account</label>
                <input
                  data-testid="emp-bank-account"
                  type="text"
                  className={`${inputCls} w-full`}
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                />
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="emp-pcb-eligible-label">
              <input
                data-testid="emp-pcb-eligible"
                type="checkbox"
                className="mt-1 accent-matcha-dark"
                checked={pcbEligible}
                onChange={(e) => setPcbEligible(e.target.checked)}
              />
              <span className="text-xs text-ink leading-tight">
                Deduct PCB (Monthly Tax)
                <span className="block text-[10px] text-ink-soft mt-0.5">
                  Enable when the employee's annual chargeable income exceeds the LHDN threshold.
                  Leave off for low-income staff — matches the sample payslip.
                </span>
              </span>
            </label>
          </div>
          <div className="pt-2 border-t border-ink/10 space-y-3">
            <p className="overline text-matcha-dark">Assigned Outlets</p>
            <p className="text-[10px] text-ink-soft -mt-2">Staff can scan and receive stock at any outlet ticked below. Leave all unticked for HQ-only staff.</p>
            {outlets.length === 0 ? (
              <p className="text-xs text-ink-soft italic" data-testid="emp-outlets-empty">
                No outlets configured yet. Add them under <strong>Operations → Inventory → Outlets</strong>.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="emp-outlets-grid">
                {outlets.map((o) => {
                  const checked = outletCodes.includes(o.code);
                  return (
                    <label
                      key={o.code}
                      data-testid={`emp-outlet-${o.code}`}
                      className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-sm border transition-colors ${checked ? "bg-matcha-dark/10 border-matcha-dark/40" : "border-ink/10 hover:border-ink/30"}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-matcha-dark"
                        checked={checked}
                        onChange={() => toggleOutlet(o.code)}
                      />
                      <span className="text-xs text-ink">
                        <span className="font-mono text-ink-soft">{o.code}</span> · {o.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
            <button data-testid="emp-save" className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AttendanceTab() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, s] = await Promise.all([
      api.get(`/attendance?month=${month}`).then((r) => r.data),
      api.get(`/staff`).then((r) => r.data),
    ]);
    setRows(a);
    setStaff(s);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const del = async (id) => {
    if (!window.confirm("Delete this attendance entry?")) return;
    await api.delete(`/attendance/${id}`);
    load();
  };

  const totalsByUser = useMemo(() => {
    const out = {};
    for (const r of rows) {
      out[r.user_name || r.user_id] = (out[r.user_name || r.user_id] || 0) + (r.hours || 0);
    }
    return out;
  }, [rows]);

  return (
    <div>
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <label className="overline text-ink-soft">Month</label>
        <input data-testid="month-filter" type="month" className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)} />
        <button
          data-testid="admin-att-add-btn"
          onClick={() => { setEditing(null); setModalOpen(true); }}
          className="ml-auto inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
        >
          + Add Shift
        </button>
      </div>
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <div className="bg-bone border border-ink/10 rounded-sm p-6">
            <p className="overline text-matcha-dark mb-3">Totals · {month}</p>
            {Object.entries(totalsByUser).length === 0 && <p className="text-ink-soft text-sm">No data.</p>}
            {Object.entries(totalsByUser).map(([name, h]) => (
              <div key={name} className="flex items-center justify-between py-2 border-b border-ink/5 last:border-0">
                <span className="text-ink text-sm">{name}</span>
                <span className="text-matcha-dark text-sm">{h.toFixed(2)}h</span>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-8 bg-bone border border-ink/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sage/40">
              <tr className="text-left text-ink-soft">
                <th className="px-3 py-3 overline">Staff</th>
                <th className="px-3 py-3 overline">Date</th>
                <th className="px-3 py-3 overline">Start</th>
                <th className="px-3 py-3 overline">End</th>
                <th className="px-3 py-3 overline">Break</th>
                <th className="px-3 py-3 overline">Hours</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-ink/5" data-testid={`admin-att-row-${r.id}`}>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {r.user_name || r.user_id}
                    {(r.edited_by_admin || r.created_by_admin) && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-matcha-dark" title={r.edited_by_admin_name || r.created_by_admin_name}>· admin</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.start_time}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{r.end_time}</td>
                  <td className="px-3 py-3 text-ink-soft whitespace-nowrap">
                    {r.break_start && r.break_end
                      ? `${r.break_start}–${r.break_end} (${r.break_minutes || 0}m)`
                      : r.break_minutes ? `${r.break_minutes}m` : "—"}
                  </td>
                  <td className="px-3 py-3 text-matcha-dark whitespace-nowrap">{r.hours}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`admin-att-edit-${r.id}`}
                      onClick={() => { setEditing(r); setModalOpen(true); }}
                      className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      data-testid={`admin-att-del-${r.id}`}
                      onClick={() => del(r.id)}
                      className="text-ink-soft hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-ink-soft">No attendance recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modalOpen && (
        <AdminAttendanceModal
          item={editing}
          staff={staff}
          defaultMonth={month}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSaved={() => { setModalOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

const BREAK_PRESETS = [
  { v: 0, label: "None" },
  { v: 30, label: "30 min" },
  { v: 45, label: "45 min" },
  { v: 60, label: "1 hour" },
];

function AdminAttendanceModal({ item, staff, defaultMonth, onClose, onSaved }) {
  const isEdit = !!item;
  const todayInMonth = `${defaultMonth}-${String(new Date().getDate()).padStart(2, "0")}`;
  const [form, setForm] = useState({
    user_id: item?.user_id || (staff[0]?.id ?? ""),
    date: item?.date || todayInMonth,
    start_time: item?.start_time || "11:00",
    end_time: item?.end_time || "21:00",
    break_minutes: item?.break_minutes ?? 0,
    notes: item?.notes || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/admin/attendance", {
        user_id: form.user_id,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: Number(form.break_minutes) || 0,
        notes: form.notes,
      });
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="admin-att-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-md rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
            {isEdit ? "Edit Shift" : "Add Shift on Behalf"}
          </h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline text-ink-soft block mb-2">Staff</label>
            <select
              data-testid="admin-att-staff"
              required
              disabled={isEdit}
              className={inputCls + " w-full disabled:opacity-60"}
              value={form.user_id}
              onChange={(e) => setForm((s) => ({ ...s, user_id: e.target.value }))}
            >
              <option value="">— Select staff —</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || s.email}{s.employment_type ? ` · ${s.employment_type.replace("_", " ")}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Date</label>
            <input
              data-testid="admin-att-date"
              required type="date"
              className={inputCls + " w-full"}
              value={form.date}
              onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-ink-soft block mb-2">Start</label>
              <input
                data-testid="admin-att-start"
                required type="time"
                className={inputCls + " w-full"}
                value={form.start_time}
                onChange={(e) => setForm((s) => ({ ...s, start_time: e.target.value }))}
              />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-2">End</label>
              <input
                data-testid="admin-att-end"
                required type="time"
                className={inputCls + " w-full"}
                value={form.end_time}
                onChange={(e) => setForm((s) => ({ ...s, end_time: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Break</label>
            <div className="grid grid-cols-4 gap-2">
              {BREAK_PRESETS.map((b) => (
                <button
                  key={b.v}
                  type="button"
                  data-testid={`admin-att-break-${b.v}`}
                  onClick={() => setForm((s) => ({ ...s, break_minutes: b.v }))}
                  className={`py-2 rounded-sm border text-xs uppercase tracking-[0.18em] transition-colors ${
                    Number(form.break_minutes) === b.v
                      ? "bg-ink text-bone border-ink"
                      : "border-ink/20 text-ink-soft hover:border-ink/40"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Notes (optional)</label>
            <input
              data-testid="admin-att-notes"
              className={inputCls + " w-full"}
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
            />
          </div>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-4">{err}</p>}
        {isEdit && (
          <p className="text-xs text-ink-soft mt-4">
            Same staff + date will overwrite the existing entry.
          </p>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button data-testid="admin-att-save" disabled={busy} className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Add Shift"}
          </button>
        </div>
      </form>
    </div>
  );
}

function daysInMonth(monthStr) {
  // monthStr = "YYYY-MM"; returns days count
  const y = parseInt(monthStr.slice(0, 4), 10);
  const m = parseInt(monthStr.slice(5, 7), 10);
  return new Date(y, m, 0).getDate();
}

function computeIncludedAmount(row, excludedSet, month, baseOverride) {
  const all = row.attendance || [];
  const included = all.filter((a) => !excludedSet.has(a.id));
  const hours = included.reduce((s, a) => s + Number(a.hours || 0), 0);
  const includedDays = new Set(included.map((a) => a.date)).size;
  if (row.employment_type === "part_time") {
    const rate = Number(row.hourly_rate || 0);
    // Per spec, PT gets no holiday multiplier — base = hours × rate, all of it
    return {
      amount: Math.round(hours * rate * 100) / 100,
      hours: Math.round(hours * 100) / 100,
      days: includedDays,
      basePay: Math.round(hours * rate * 100) / 100,
      holidayPay: 0,
      otPay: 0,
      regularDays: includedDays,
      holidayDays: 0,
      otHours: 0,
      included,
    };
  }
  if (row.employment_type === "full_time") {
    const dailyRate = row.daily_rate != null ? Number(row.daily_rate) : Number(row.basic_salary || 0) / 26;
    const phMult = row.ph_multiplier || 2;
    const otRate = row.ot_hourly_rate || 8;
    const otMult = row.ot_multiplier || 1.5;
    const stdHoursPerDay = 8;
    // De-dup by date (sum hours per day, take is_holiday from any entry)
    const byDate = {};
    included.forEach((a) => {
      if (!byDate[a.date]) byDate[a.date] = { is_holiday: !!a.is_holiday, hours: 0 };
      byDate[a.date].hours += Number(a.hours || 0);
      if (a.is_holiday) byDate[a.date].is_holiday = true;
    });
    const dates = Object.values(byDate);
    const regularDays = dates.filter((d) => !d.is_holiday).length;
    const holidayDays = dates.filter((d) => d.is_holiday).length;
    const otHours = dates.reduce(
      (s, d) => s + (d.is_holiday ? 0 : Math.max(0, d.hours - stdHoursPerDay)),
      0
    );
    const computedBase = Math.round(regularDays * dailyRate * 100) / 100;
    // Admin BASE override (per-month) replaces the computed figure so
    // fixed-salary FT staff still get their contracted amount even when
    // some days are unticked.
    const basePay = baseOverride && Number(baseOverride) > 0
      ? Math.round(Number(baseOverride) * 100) / 100
      : computedBase;
    const holidayPay = Math.round(holidayDays * phMult * dailyRate * 100) / 100;
    const otPay = Math.round(otHours * otRate * otMult * 100) / 100;
    const amount = Math.round((basePay + holidayPay + otPay) * 100) / 100;
    return {
      amount,
      hours: Math.round(hours * 100) / 100,
      days: includedDays,
      regularDays,
      holidayDays,
      otHours: Math.round(otHours * 100) / 100,
      basePay,
      computedBase,
      holidayPay,
      otPay,
      baseOverridden: !!(baseOverride && Number(baseOverride) > 0),
      included,
    };
  }
  return { amount: 0, hours: 0, days: includedDays, basePay: 0, holidayPay: 0, otPay: 0, included };
}

function exportPayrollPDF(row, comp, month) {
  const name = row.full_name || row.preferred_name || row.email || "Staff";
  const rate = row.employment_type === "part_time"
    ? `Hourly rate · RM ${Number(row.hourly_rate || 0).toFixed(2)}/h`
    : row.employment_type === "full_time"
      ? `Monthly base · RM ${Number(row.basic_salary || 0).toFixed(2)}`
      : "";
  const included = comp.included || [];
  const rowsHtml = included.map((a) => {
    const dayPay = Number(a.day_pay || 0) + Number(a.ot_pay || 0);
    const notes = [];
    if (a.is_holiday) notes.push("Holiday 2×");
    if (a.break_minutes > 0) notes.push(`${a.break_minutes}m break`);
    if (a.ot_hours > 0) notes.push(`${a.ot_hours}h OT`);
    return `
      <tr>
        <td>${a.date}</td>
        <td>${a.start_time || "—"} – ${a.end_time || "—"}</td>
        <td class="num">${a.hours || 0}h</td>
        <td>${notes.join(" · ") || "—"}</td>
        <td class="num">RM ${dayPay.toFixed(2)}</td>
      </tr>`;
  }).join("");

  const summaryPills = [
    comp.basePay > 0 ? `<span class="pill">Base · RM ${comp.basePay.toFixed(2)}</span>` : "",
    comp.holidayPay > 0 ? `<span class="pill pill-accent">Holiday 2× · RM ${comp.holidayPay.toFixed(2)} · ${comp.holidayDays}d</span>` : "",
    comp.otPay > 0 ? `<span class="pill pill-accent">OT 1.5× · RM ${comp.otPay.toFixed(2)} · ${comp.otHours}h</span>` : "",
  ].filter(Boolean).join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8" />
<title>Payslip · ${name} · ${month}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; margin: 32px; }
  .head { border-bottom: 2px solid #1a1a1a; padding-bottom: 14px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: baseline; }
  .brand { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; color: #6b6b6b; }
  h1 { font-size: 22px; margin: 4px 0 0; letter-spacing: 0.04em; }
  .meta { font-size: 11px; color: #6b6b6b; margin-top: 4px; }
  .sub { font-size: 12px; color: #444; margin: 10px 0 18px; }
  .pills { margin: 12px 0 22px; }
  .pill { display: inline-block; padding: 6px 12px; border: 1px solid #d5d5d5; border-radius: 999px; font-size: 11px; margin-right: 6px; }
  .pill-accent { border-color: #2f5233; color: #2f5233; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #eee; }
  th { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: #6b6b6b; border-bottom: 1px solid #1a1a1a; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td { border-top: 2px solid #1a1a1a; font-weight: 700; font-size: 14px; padding-top: 12px; }
  .foot { margin-top: 32px; font-size: 10px; color: #999; display: flex; justify-content: space-between; }
  @media print { body { margin: 18mm; } }
</style>
</head><body>
  <div class="head">
    <div>
      <div class="brand">Matcha Story · Payslip</div>
      <h1>${name}</h1>
      <div class="meta">Period · ${month} · ${row.employment_type ? row.employment_type.replace("_", " ").toUpperCase() : ""}</div>
    </div>
    <div style="text-align:right;">
      <div class="brand">Total</div>
      <div style="font-size:26px; letter-spacing:0.02em;">RM ${comp.amount.toFixed(2)}</div>
      <div class="meta">${included.length} of ${(row.attendance || []).length} dates · ${comp.hours}h</div>
    </div>
  </div>
  ${rate ? `<div class="sub">${rate}</div>` : ""}
  <div class="pills">${summaryPills}</div>
  <table>
    <thead>
      <tr><th>Date</th><th>Time</th><th class="num">Hours</th><th>Notes</th><th class="num">Amount</th></tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="5" style="text-align:center;color:#999;padding:24px;">No dates included</td></tr>`}</tbody>
    <tfoot>
      <tr><td colspan="4">Total payable</td><td class="num">RM ${comp.amount.toFixed(2)}</td></tr>
    </tfoot>
  </table>
  <div class="foot">
    <span>Generated ${new Date().toLocaleString()}</span>
    <span>Matcha Story FMS</span>
  </div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body></html>`;

  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) { alert("Please allow pop-ups to export the payslip."); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

function PayrollTab() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // { user_id: true }
  const [expanded, setExpanded] = useState({}); // { user_id: true }
  const [excluded, setExcluded] = useState({}); // { user_id: Set<attendance_id> }
  const [overrides, setOverrides] = useState({}); // { user_id: base_override }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [payslipFor, setPayslipFor] = useState(null);

  const load = async () => {
    const [{ data }, { data: ov }] = await Promise.all([
      api.get(`/payroll?month=${month}`),
      api.get(`/payroll/overrides?month=${month}`),
    ]);
    setRows(data);
    setOverrides(
      Object.fromEntries(
        Object.entries(ov || {}).map(([uid, o]) => [uid, Number(o.base_override || 0)]),
      ),
    );
    setSelected({});
    setExcluded({});
    setExpanded({});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const saveOverride = async (userId, newVal) => {
    await api.patch(`/payroll/overrides/${userId}`, {
      month,
      base_override: newVal && Number(newVal) > 0 ? Number(newVal) : null,
    });
    setOverrides((s) => {
      const next = { ...s };
      if (newVal && Number(newVal) > 0) next[userId] = Number(newVal);
      else delete next[userId];
      return next;
    });
  };

  // Eligible: has employment_type AND not yet paid AND any attendance
  const eligible = (r) => !!r.employment_type && !r.paid && (r.attendance || []).length > 0;
  const eligibleRows = rows.filter(eligible);

  const getComputed = (r) => computeIncludedAmount(
    r,
    excluded[r.user_id] || new Set(),
    month,
    overrides[r.user_id],
  );

  const selectedRows = rows.filter((r) => selected[r.user_id] && eligible(r));
  const selectedTotal = selectedRows.reduce((s, r) => s + getComputed(r).amount, 0);
  const allEligibleChecked = eligibleRows.length > 0 && eligibleRows.every((r) => selected[r.user_id]);

  const grand = rows.reduce((s, r) => s + (r.estimated_pay || 0), 0);
  const paidTotal = rows.reduce((s, r) => s + (r.paid ? Number(r.payment?.amount || 0) : 0), 0);

  const toggleAll = () => {
    if (allEligibleChecked) {
      setSelected({});
    } else {
      const next = {};
      eligibleRows.forEach((r) => { next[r.user_id] = true; });
      setSelected(next);
    }
  };

  const toggleAttendance = (userId, attId) => {
    setExcluded((s) => {
      const next = { ...s };
      const cur = new Set(next[userId] || []);
      if (cur.has(attId)) cur.delete(attId); else cur.add(attId);
      next[userId] = cur;
      return next;
    });
  };

  const toggleAllAttendance = (row) => {
    const cur = excluded[row.user_id] || new Set();
    const allIds = (row.attendance || []).map((a) => a.id);
    const allExcluded = allIds.length > 0 && allIds.every((id) => cur.has(id));
    setExcluded((s) => ({
      ...s,
      [row.user_id]: allExcluded ? new Set() : new Set(allIds),
    }));
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <label className="overline text-ink-soft">Month</label>
        <input data-testid="month-filter" type="month" className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)} />
        <div className="ml-auto flex items-center gap-6 flex-wrap">
          <span className="overline text-ink-soft">
            Estimated: <span className="text-ink text-base ml-1">RM {grand.toFixed(2)}</span>
          </span>
          <span className="overline text-ink-soft">
            Paid: <span className="text-matcha-dark text-base ml-1">RM {paidTotal.toFixed(2)}</span>
          </span>
        </div>
      </div>

      <div
        className={`flex items-center justify-between gap-4 mb-4 px-5 py-3 rounded-sm border transition-colors ${
          selectedRows.length > 0 ? "bg-matcha-dark text-bone border-matcha-dark" : "bg-cream/40 text-ink-soft border-ink/10"
        }`}
        data-testid="payroll-selection-bar"
      >
        <div className="flex items-center gap-6 text-sm flex-wrap">
          <span data-testid="payroll-selected-count">
            <span className="font-display text-lg" style={{ letterSpacing: "0.02em" }}>{selectedRows.length}</span>
            <span className="overline ml-2">selected</span>
          </span>
          <span data-testid="payroll-selected-total">
            <span className="overline">Total</span>
            <span className="font-display text-lg ml-2" style={{ letterSpacing: "0.02em" }}>RM {selectedTotal.toFixed(2)}</span>
          </span>
        </div>
        <button
          data-testid="payroll-bulk-pay-btn"
          disabled={selectedRows.length === 0 || selectedTotal <= 0}
          onClick={() => setBulkOpen(true)}
          className={`inline-flex items-center gap-2 rounded-full px-6 py-2 text-xs uppercase tracking-[0.22em] transition-colors ${
            selectedRows.length > 0 && selectedTotal > 0
              ? "bg-bone text-ink hover:bg-bone/90"
              : "bg-ink/10 text-ink-soft cursor-not-allowed"
          }`}
        >
          Mark Selected as Paid
        </button>
      </div>

      <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="payroll-select-all"
                  checked={allEligibleChecked}
                  onChange={toggleAll}
                  disabled={eligibleRows.length === 0}
                  className="w-4 h-4 accent-matcha-dark cursor-pointer disabled:cursor-not-allowed"
                />
              </th>
              <th className="px-2 py-3 w-8"></th>
              <th className="px-3 py-3 overline">Staff</th>
              <th className="px-3 py-3 overline">Days</th>
              <th className="px-3 py-3 overline">Hours</th>
              <th className="px-3 py-3 overline">Rate / Salary</th>
              <th className="px-3 py-3 overline text-right">Amount</th>
              <th className="px-3 py-3 overline">Status</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const can = eligible(r);
              const isSelected = !!selected[r.user_id];
              const isExpanded = !!expanded[r.user_id];
              const exSet = excluded[r.user_id] || new Set();
              const comp = getComputed(r);
              const hasExclusion = exSet.size > 0;
              const orig = Number(r.estimated_pay || 0);
              return (
                <Fragment key={r.user_id}>
                  <tr
                    className={`border-t border-ink/5 transition-colors ${isSelected ? "bg-sage/20" : ""}`}
                    data-testid={`payroll-row-${r.user_id}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={`payroll-check-${r.user_id}`}
                        checked={isSelected}
                        disabled={!can}
                        onChange={(e) => setSelected((s) => ({ ...s, [r.user_id]: e.target.checked }))}
                        className="w-4 h-4 accent-matcha-dark cursor-pointer disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-2 py-3">
                      {(r.attendance || []).length > 0 && (
                        <button
                          type="button"
                          data-testid={`payroll-expand-${r.user_id}`}
                          onClick={() => setExpanded((s) => ({ ...s, [r.user_id]: !s[r.user_id] }))}
                          className={`text-ink-soft hover:text-ink transition-transform inline-block ${isExpanded ? "rotate-90 text-matcha-dark" : ""}`}
                          aria-label="Show dates"
                        >
                          <ChevronRight size={16} />
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-ink">{r.full_name}</span>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">{r.employment_type || "no employment"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">{comp.days}{hasExclusion && <span className="text-ink-soft text-[10px] ml-1">/ {r.days_worked}</span>}</td>
                    <td className="px-3 py-3">{comp.hours}{hasExclusion && <span className="text-ink-soft text-[10px] ml-1">/ {r.total_hours}</span>}</td>
                    <td className="px-3 py-3 text-ink-soft whitespace-nowrap">
                      <BasePayCell row={r} onSaved={load} />
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap" data-testid={`payroll-amount-${r.user_id}`}>
                      {hasExclusion ? (
                        <div className="flex flex-col items-end">
                          <span className="text-matcha-dark">RM {comp.amount.toFixed(2)}</span>
                          <span className="text-[10px] text-ink-soft line-through">RM {orig.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="text-ink">RM {orig.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {r.paid ? (
                        <div className="flex flex-col">
                          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-matcha-dark" data-testid={`payroll-status-paid-${r.user_id}`}>
                            <Check size={14} /> Paid
                          </span>
                          <span className="text-[10px] text-ink-soft mt-0.5">
                            RM {Number(r.payment?.amount || 0).toFixed(2)} · {r.payment?.paid_date}
                          </span>
                        </div>
                      ) : !r.employment_type ? (
                        <span className="text-xs uppercase tracking-[0.18em] text-ink-soft/60">No employment</span>
                      ) : (
                        <span className="text-xs uppercase tracking-[0.18em] text-ink-soft" data-testid={`payroll-status-unpaid-${r.user_id}`}>Unpaid</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {r.paid && (
                        <button
                          data-testid={`payroll-view-payment-${r.user_id}`}
                          onClick={() => setViewing(r)}
                          className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-cream/40" data-testid={`payroll-dates-panel-${r.user_id}`}>
                      <td colSpan={9} className="px-6 py-4">
                        <p className="overline text-ink-soft mb-3">Select dates to include in this payment · uncheck to skip</p>
                        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`payroll-breakdown-${r.user_id}`}>
                          {comp.basePay > 0 && (
                            <BasePill
                              row={r}
                              comp={comp}
                              onSave={(v) => saveOverride(r.user_id, v)}
                            />
                          )}
                          {comp.holidayPay > 0 && (
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bone border border-matcha-dark/40 text-xs">
                              <span className="overline text-matcha-dark">Holiday 2×</span>
                              <span className="text-ink">RM {comp.holidayPay.toFixed(2)}</span>
                              <span className="text-[10px] text-ink-soft">· {comp.holidayDays}d</span>
                            </span>
                          )}
                          {comp.otPay > 0 && (
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bone border border-matcha-dark/40 text-xs">
                              <span className="overline text-matcha-dark">OT 1.5×</span>
                              <span className="text-ink">RM {comp.otPay.toFixed(2)}</span>
                              <span className="text-[10px] text-ink-soft">· {comp.otHours}h</span>
                            </span>
                          )}
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-matcha-dark text-bone text-xs">
                            <span className="overline">= Total</span>
                            <span className="font-display text-sm" style={{ letterSpacing: "0.02em" }}>RM {comp.amount.toFixed(2)}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                          <button
                            type="button"
                            data-testid={`payroll-toggle-all-dates-${r.user_id}`}
                            onClick={() => toggleAllAttendance(r)}
                            className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline"
                          >
                            {(r.attendance || []).length > 0 && (r.attendance || []).every((a) => exSet.has(a.id)) ? "Select All" : "Clear All"}
                          </button>
                          <span className="text-xs text-ink-soft">
                            {comp.included.length} of {(r.attendance || []).length} dates · {comp.hours}h
                          </span>
                          <button
                            type="button"
                            data-testid={`payroll-export-pdf-${r.user_id}`}
                            onClick={() => exportPayrollPDF(r, comp, month)}
                            disabled={comp.included.length === 0}
                            className="ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 border border-ink/20 text-ink text-[10px] uppercase tracking-[0.18em] hover:bg-ink/5 transition-colors disabled:opacity-40"
                          >
                            <Download size={12} /> Attendance PDF
                          </button>
                          <button
                            type="button"
                            data-testid={`payroll-generate-payslip-${r.user_id}`}
                            onClick={() => setPayslipFor({
                              row: r,
                              excludedDates: (r.attendance || [])
                                .map((a) => a.date)
                                .filter((d) => !(comp.included || []).some((inc) => inc.date === d)),
                            })}
                            disabled={!r.employment_type}
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 bg-ink text-bone text-[10px] uppercase tracking-[0.18em] hover:bg-matcha-dark transition-colors disabled:opacity-40"
                          >
                            <FileText size={12} /> Generate Payslip
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {(r.attendance || []).map((a) => {
                            const included = !exSet.has(a.id);
                            return (
                              <label
                                key={a.id}
                                data-testid={`payroll-date-row-${r.user_id}-${a.date}`}
                                className={`flex items-center gap-3 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${
                                  included ? "border-matcha-dark/40 bg-bone" : "border-ink/10 bg-ink/5 opacity-60"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  data-testid={`payroll-date-check-${r.user_id}-${a.date}`}
                                  checked={included}
                                  onChange={() => toggleAttendance(r.user_id, a.id)}
                                  className="w-4 h-4 accent-matcha-dark"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-ink flex items-center justify-between gap-2">
                                    <span>
                                      {a.date}
                                      {a.is_holiday && (
                                        <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-matcha-dark">Public Holiday · 2×</span>
                                      )}
                                    </span>
                                    {(Number(a.day_pay || 0) + Number(a.ot_pay || 0)) > 0 && (
                                      <span className="text-matcha-dark text-sm whitespace-nowrap" data-testid={`payroll-day-pay-${r.user_id}-${a.date}`}>
                                        RM {(Number(a.day_pay || 0) + Number(a.ot_pay || 0)).toFixed(2)}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em]">
                                    {a.start_time}–{a.end_time} · {a.hours}h
                                    {a.break_minutes > 0 && ` · ${a.break_minutes}m break`}
                                    {a.ot_hours > 0 && <span className="text-matcha-dark"> · {a.ot_hours}h OT (RM {Number(a.ot_pay || 0).toFixed(2)})</span>}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-ink-soft">No staff yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {bulkOpen && (
        <BulkMarkPaidModal
          rows={selectedRows.map((r) => {
            const comp = getComputed(r);
            const allIds = (r.attendance || []).map((a) => a.id);
            const exSet = excluded[r.user_id] || new Set();
            return {
              ...r,
              computedAmount: comp.amount,
              computedHours: comp.hours,
              computedDays: comp.days,
              attendanceIds: allIds.filter((id) => !exSet.has(id)),
            };
          })}
          month={month}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); load(); }}
        />
      )}
      {viewing && (
        <PaymentDetailsModal
          row={viewing}
          onClose={() => setViewing(null)}
          onDeleted={() => { setViewing(null); load(); }}
        />
      )}
      {payslipFor && (
        <PayslipModal
          row={payslipFor.row}
          excludedDates={payslipFor.excludedDates || []}
          month={month}
          onClose={() => setPayslipFor(null)}
        />
      )}
    </div>
  );
}

// ---------------- Inline-edit BASE pill (per-month override) ----------------
// Click the BASE pill in the expanded Payroll panel to type a fixed amount
// for THIS month only (e.g. keep RM 1,700 even when a few days are
// unticked). Clearing the field reverts to the computed daily × days.
function BasePill({ row, comp, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const isFT = row.employment_type === "full_time";

  const begin = () => {
    setVal(String(comp.basePay || ""));
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(val);
      setEditing(false);
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await onSave(null);
      setEditing(false);
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-bone border border-matcha-dark text-xs"
        onClick={(e) => e.stopPropagation()}
        data-testid={`base-pill-edit-${row.user_id}`}
      >
        <span className="overline text-matcha-dark">Base</span>
        <span className="text-[10px] text-ink-soft">RM</span>
        <input
          data-testid={`base-pill-input-${row.user_id}`}
          type="number"
          min="0"
          step="0.01"
          autoFocus
          disabled={busy}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); save(); }
            if (e.key === "Escape") { setEditing(false); }
          }}
          className="w-20 px-1 py-0.5 rounded-sm border border-ink/20 text-xs text-ink text-right"
        />
        <button
          type="button"
          data-testid={`base-pill-save-${row.user_id}`}
          onClick={save}
          disabled={busy}
          className="p-0.5 rounded hover:bg-matcha-dark/10 text-matcha-dark disabled:opacity-40"
          title="Save override"
        >
          <Check size={12} />
        </button>
        {comp.baseOverridden && (
          <button
            type="button"
            data-testid={`base-pill-clear-${row.user_id}`}
            onClick={clearOverride}
            disabled={busy}
            className="text-[10px] text-ink-soft uppercase tracking-[0.14em] hover:text-ink disabled:opacity-40"
            title="Clear override and use computed"
          >
            Reset
          </button>
        )}
        <button
          type="button"
          data-testid={`base-pill-cancel-${row.user_id}`}
          onClick={() => setEditing(false)}
          className="p-0.5 rounded hover:bg-ink/5 text-ink-soft"
          title="Cancel"
        >
          <X size={12} />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid={`base-pill-${row.user_id}`}
      onClick={(e) => { e.stopPropagation(); begin(); }}
      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bone text-xs transition-colors ${
        comp.baseOverridden
          ? "border border-matcha-dark/60 hover:bg-matcha-dark/5"
          : "border border-ink/10 hover:border-matcha-dark/40"
      }`}
      title="Click to edit base amount for this month"
    >
      <span className={`overline ${comp.baseOverridden ? "text-matcha-dark" : "text-ink-soft"}`}>Base</span>
      <span className="text-ink">RM {comp.basePay.toFixed(2)}</span>
      {isFT && <span className="text-[10px] text-ink-soft">· {comp.regularDays}d</span>}
      {comp.baseOverridden && (
        <span
          className="text-[9px] uppercase tracking-[0.14em] text-matcha-dark"
          data-testid={`base-pill-overridden-${row.user_id}`}
        >
          Override
        </span>
      )}
      <Pencil size={10} className="opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
}


// ---------------- Inline-edit base pay from the payroll row ----------------
// Click to swap into an input; Enter saves to /api/staff/{id}/employment and
// re-fetches the payroll table so Daily Rate, OT Hourly Rate, Base Pay and
// the payslip all reflect the new value. Only the base/rate changes — all
// other employment fields (type, OT override, position, etc.) are preserved.
function BasePayCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const isFT = row.employment_type === "full_time";
  const isPT = row.employment_type === "part_time";

  if (!row.employment_type) return <span>—</span>;

  const begin = () => {
    setVal(String(isFT ? (row.basic_salary || "") : (row.hourly_rate || "")));
    setEditing(true);
  };

  const save = async () => {
    const n = parseFloat(val);
    if (!(n > 0)) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/staff/${row.user_id}/employment`, {
        employment_type: row.employment_type,
        basic_salary: isFT ? n : null,
        hourly_rate: isPT ? n : null,
        normal_hours_per_day: row.normal_hours_per_day ?? null,
        ot_hourly_rate: row.ot_hourly_rate ?? null,
        // Preserve payslip fields so the PATCH doesn't blank them
        date_joined: row.date_joined || null,
        position: row.position || null,
        bank_name: row.bank_name || null,
        bank_account: row.bank_account || null,
        pcb_eligible: !!row.pcb_eligible,
      });
      setEditing(false);
      await onSaved();
    } catch (e) {
      alert(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`base-edit-${row.user_id}`} onClick={(e) => e.stopPropagation()}>
        <span className="text-[10px] text-ink-soft">RM</span>
        <input
          data-testid={`base-input-${row.user_id}`}
          type="number"
          min="0"
          step="0.01"
          autoFocus
          disabled={busy}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); save(); }
            if (e.key === "Escape") { setEditing(false); }
          }}
          className="w-24 px-2 py-1 rounded-sm border border-matcha-dark text-xs text-ink"
        />
        <span className="text-[10px] text-ink-soft">{isFT ? "/mo" : "/hr"}</span>
        <button
          type="button"
          data-testid={`base-save-${row.user_id}`}
          onClick={save}
          disabled={busy}
          className="p-1 rounded hover:bg-matcha-dark/10 text-matcha-dark disabled:opacity-40"
        >
          <Check size={12} />
        </button>
        <button
          type="button"
          data-testid={`base-cancel-${row.user_id}`}
          onClick={() => setEditing(false)}
          className="p-1 rounded hover:bg-ink/5 text-ink-soft"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={`base-view-${row.user_id}`}
      onClick={(e) => { e.stopPropagation(); begin(); }}
      className="text-left group hover:text-ink transition-colors"
      title="Click to edit base pay"
    >
      <span className="inline-flex items-center gap-1">
        {isFT ? `RM ${Number(row.basic_salary || 0).toFixed(2)}/mo`
              : `RM ${Number(row.hourly_rate || 0).toFixed(2)}/hr`}
        <Pencil size={10} className="opacity-0 group-hover:opacity-60 transition-opacity" />
      </span>
      {isFT && (
        <span className="block text-[10px] uppercase tracking-[0.18em] text-matcha-dark">
          RM {Number(row.daily_rate || (row.basic_salary || 0) / 26).toFixed(2)}/day · OT RM {row.ot_hourly_rate || 8}/h
        </span>
      )}
    </button>
  );
}


// ---------------- Malaysia Payslip modal ----------------
// Renders a full Malaysia-format payslip (earnings / deductions / employer
// contributions / net salary) matching the sample "2026.08 Progressive
// Synergy SB - Payslip" layout. Prints to PDF via browser Save-as-PDF.
function PayslipModal({ row, excludedDates = [], month, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const excludedKey = excludedDates.join(",");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ month });
        if (excludedKey) params.set("exclude_dates", excludedKey);
        const { data: p } = await api.get(
          `/payroll/payslip/${row.user_id}?${params.toString()}`,
        );
        if (!cancelled) setData(p);
      } catch (e) {
        if (!cancelled) setErr(formatErr(e?.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [row.user_id, month, excludedKey]);

  const money = (v) => Number(v || 0).toLocaleString("en-MY", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const monthName = (m) => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    const names = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];
    return `${names[parseInt(mo, 10) - 1]} ${y}`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mo}-${yy}`;
  };

  const print = () => {
    if (!data) return;
    const html = buildPayslipHTML(data, { monthName: monthName(data.month), fmtDate, money });
    // Use a hidden iframe (survives pop-up blockers) → trigger the
    // browser's native print dialog where admin can Save as PDF or
    // send to a physical printer.
    let frame = document.getElementById("payslip-print-frame");
    if (!frame) {
      frame = document.createElement("iframe");
      frame.id = "payslip-print-frame";
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      document.body.appendChild(frame);
    }
    const doc = frame.contentDocument || frame.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    // Wait for the iframe's load event so Chrome/Safari register the
    // DOM before invoking print — otherwise the dialog opens blank.
    const trigger = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (e) { /* iframe gone */ }
    };
    if (doc.readyState === "complete") setTimeout(trigger, 100);
    else frame.onload = () => setTimeout(trigger, 100);
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6" data-testid="payslip-modal">
      <div className="bg-bone rounded-sm p-8 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-ink-soft">Payslip Preview</p>
            <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
              {row.full_name} · {monthName(month)}
            </h3>
          </div>
          <button type="button" data-testid="payslip-close" onClick={onClose} className="text-ink-soft hover:text-ink">
            <X size={18} />
          </button>
        </div>

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4" data-testid="payslip-err">{err}</p>}
        {!data && !err && <p className="text-ink-soft text-sm">Generating…</p>}

        {excludedDates.length > 0 && (
          <p
            className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm mb-4"
            data-testid="payslip-excluded-banner"
          >
            Excluding {excludedDates.length} unticked day{excludedDates.length === 1 ? "" : "s"}
            {" · "}
            <span className="font-mono">{excludedDates.join(", ")}</span>
          </p>
        )}

        {data && (
          <>
            <div className="border border-ink/20 bg-white p-6 text-[12px] font-sans text-ink mb-4" data-testid="payslip-preview">
              <div className="text-center mb-4">
                <p className="font-bold text-sm tracking-wide">{data.company.name}</p>
                <p className="text-[11px] text-ink-soft">({data.company.reg_no})</p>
                {data.company.address && (
                  <p className="text-[11px] text-ink-soft mt-0.5">{data.company.address}</p>
                )}
              </div>
              <div className="text-center text-[11px] uppercase tracking-[0.14em] text-ink border-y border-ink/20 py-1 mb-3">
                For the month of {monthName(data.month)}
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] mb-4">
                <div><span className="text-ink-soft">Name:</span> <strong>{data.employee.name}</strong></div>
                <div><span className="text-ink-soft">Date Joined:</span> {fmtDate(data.employee.date_joined)}</div>
                <div><span className="text-ink-soft">I/C No.:</span> {data.employee.ic_no || "—"}</div>
                <div><span className="text-ink-soft">Position:</span> {data.employee.position}</div>
                {data.employee.bank_name && (
                  <div className="col-span-2"><span className="text-ink-soft">Bank:</span> {data.employee.bank_name} {data.employee.bank_account}</div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-bold text-[11px] uppercase tracking-[0.14em] bg-ink/10 px-2 py-1 mb-1">Earnings</p>
                  <table className="w-full text-[11px]" data-testid="payslip-earnings-table">
                    <tbody>
                      <PayslipRow label="Basic Salary"  v={data.earnings.basic_salary} testid="earn-basic" />
                      <PayslipRow label="Allowance/PH"  v={data.earnings.allowance_ph} testid="earn-ph" />
                      <PayslipRow label="Overtime"      v={data.earnings.overtime}     testid="earn-ot" />
                      <PayslipRow label="Commission"    v={data.earnings.commission}   testid="earn-comm" />
                      <PayslipRow label="Fee"           v={data.earnings.fee}          testid="earn-fee" />
                      <PayslipRow label="Bonus"         v={data.earnings.bonus}        testid="earn-bonus" />
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-ink/40 font-bold">
                        <td className="py-1">Total Income</td>
                        <td className="py-1 text-right" data-testid="earn-total">{money(data.earnings.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div>
                  <p className="font-bold text-[11px] uppercase tracking-[0.14em] bg-ink/10 px-2 py-1 mb-1">Deductions</p>
                  <table className="w-full text-[11px]" data-testid="payslip-deductions-table">
                    <tbody>
                      <PayslipRow label="E.P.F (Employee)"      v={data.deductions.epf_employee}   testid="ded-epf" />
                      <PayslipRow label="SOCSO Contribution"    v={data.deductions.socso_employee} testid="ded-socso" />
                      <PayslipRow label="SOCSO (24 hours)"      v={data.deductions.lindung_24jam}  testid="ded-lindung" />
                      <PayslipRow label="SIP Contribution"      v={data.deductions.sip_employee}   testid="ded-sip" />
                      <PayslipRow label="PCB Contribution"      v={data.deductions.pcb}            testid="ded-pcb" />
                      <PayslipRow label="Advance/Deductions"    v={data.deductions.advance}        testid="ded-advance" />
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-ink/40 font-bold">
                        <td className="py-1">Total Deductions</td>
                        <td className="py-1 text-right" data-testid="ded-total">{money(data.deductions.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t-2 border-ink flex items-center justify-between" data-testid="payslip-net-row">
                <span className="font-bold uppercase tracking-[0.14em] text-[12px]">Net Salary</span>
                <span className="font-bold text-sm" data-testid="payslip-net-value">RM {money(data.net_salary)}</span>
              </div>

              <div className="mt-4 pt-3 border-t border-ink/20">
                <p className="font-bold text-[11px] uppercase tracking-[0.14em] bg-ink/10 px-2 py-1 mb-1">Employer Contribution</p>
                <table className="w-full text-[11px]" data-testid="payslip-employer-table">
                  <tbody>
                    <PayslipRow label="EPF (Employer)"   v={data.employer_contribution.epf}   testid="emp-epf" />
                    <PayslipRow label="SOCSO (Employer)" v={data.employer_contribution.socso} testid="emp-socso" />
                    <PayslipRow label="SIP (Employer)"   v={data.employer_contribution.sip}   testid="emp-sip" />
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-ink/40 font-bold">
                      <td className="py-1">Total Employer Contribution</td>
                      <td className="py-1 text-right" data-testid="emp-total">{money(data.employer_contribution.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] text-ink-soft">
                {data.work_summary.paid_working_days} paid days · daily RM {money(data.work_summary.daily_rate)} · OT {data.work_summary.ot_hours}h × RM {money(data.work_summary.ot_hourly_rate)} × 1.5
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="payslip-cancel"
                  onClick={onClose}
                  className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
                >
                  Close
                </button>
                <button
                  type="button"
                  data-testid="payslip-download"
                  onClick={print}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
                >
                  <Download size={13} /> Print / Save PDF
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PayslipRow({ label, v, testid }) {
  const n = Number(v || 0);
  return (
    <tr>
      <td className="py-0.5">{label}</td>
      <td className="py-0.5 text-right" data-testid={testid}>
        {n > 0 ? Number(n).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
      </td>
    </tr>
  );
}

function buildPayslipHTML(data, { monthName, fmtDate, money }) {
  const row = (label, v) => `
    <tr>
      <td class="l">${label}</td>
      <td class="r">${Number(v || 0) > 0 ? money(v) : "—"}</td>
    </tr>`;
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>Payslip · ${data.employee.name} · ${data.month}</title>
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11px; }
  h1 { font-size: 14px; text-align: center; margin: 0 0 2px; }
  .reg { text-align: center; font-size: 10px; color: #555; margin-bottom: 10px; }
  .period { text-align: center; border-top: 1px solid #999; border-bottom: 1px solid #999; padding: 4px 0; font-weight: bold; letter-spacing: 1.5px; margin-bottom: 12px; }
  .emp { display: grid; grid-template-columns: 1fr 1fr; row-gap: 2px; column-gap: 20px; font-size: 11px; margin-bottom: 12px; }
  .emp span.k { color: #555; }
  .wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .box h2 { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; background: #eee; padding: 4px 6px; margin: 0 0 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  td { padding: 2px 0; }
  td.l { color: #111; }
  td.r { text-align: right; }
  tfoot td { border-top: 1px solid #666; font-weight: bold; padding-top: 4px; }
  .net { margin-top: 14px; padding-top: 8px; border-top: 2px solid #000; display: flex; justify-content: space-between; font-weight: bold; font-size: 13px; }
  .net .lbl { letter-spacing: 1.5px; text-transform: uppercase; }
  .emp-wrap { margin-top: 14px; padding-top: 8px; border-top: 1px solid #ccc; }
  .sig { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 48px; }
  .sig .line { border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 10px; }
  .foot { margin-top: 24px; text-align: center; font-size: 9px; color: #888; }
</style>
</head>
<body>
  <h1>${data.company.name}</h1>
  <div class="reg">(${data.company.reg_no})</div>
  ${data.company.address ? `<div class="reg">${data.company.address}</div>` : ""}
  <div class="period">For the month of ${monthName}</div>
  <div class="emp">
    <div><span class="k">Name:</span> <strong>${data.employee.name}</strong></div>
    <div><span class="k">Date Joined:</span> ${fmtDate(data.employee.date_joined)}</div>
    <div><span class="k">I/C No.:</span> ${data.employee.ic_no || "—"}</div>
    <div><span class="k">Position:</span> ${data.employee.position}</div>
    ${data.employee.bank_name
      ? `<div style="grid-column: span 2;"><span class="k">Bank:</span> ${data.employee.bank_name} ${data.employee.bank_account}</div>`
      : ""}
  </div>
  <div class="wrap">
    <div class="box">
      <h2>Earnings</h2>
      <table>
        <tbody>
          ${row("Basic Salary", data.earnings.basic_salary)}
          ${row("Allowance/PH", data.earnings.allowance_ph)}
          ${row("Overtime", data.earnings.overtime)}
          ${row("Commission", data.earnings.commission)}
          ${row("Fee", data.earnings.fee)}
          ${row("Bonus", data.earnings.bonus)}
        </tbody>
        <tfoot>
          <tr><td class="l">Total Income</td><td class="r">${money(data.earnings.total)}</td></tr>
        </tfoot>
      </table>
    </div>
    <div class="box">
      <h2>Deductions</h2>
      <table>
        <tbody>
          ${row("E.P.F (Employee)", data.deductions.epf_employee)}
          ${row("SOCSO Contribution", data.deductions.socso_employee)}
          ${row("SOCSO (24 hours)", data.deductions.lindung_24jam)}
          ${row("SIP Contribution", data.deductions.sip_employee)}
          ${row("PCB Contribution", data.deductions.pcb)}
          ${row("Advance/Deductions", data.deductions.advance)}
        </tbody>
        <tfoot>
          <tr><td class="l">Total Deductions</td><td class="r">${money(data.deductions.total)}</td></tr>
        </tfoot>
      </table>
    </div>
  </div>
  <div class="net"><span class="lbl">Net Salary</span><span>RM ${money(data.net_salary)}</span></div>
  <div class="emp-wrap">
    <h2 style="font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; background: #eee; padding: 4px 6px; margin: 0 0 2px;">Employer Contribution</h2>
    <table>
      <tbody>
        ${row("EPF (Employer)", data.employer_contribution.epf)}
        ${row("SOCSO (Employer)", data.employer_contribution.socso)}
        ${row("SIP (Employer)", data.employer_contribution.sip)}
      </tbody>
      <tfoot>
        <tr><td class="l">Total Employer Contribution</td><td class="r">${money(data.employer_contribution.total)}</td></tr>
      </tfoot>
    </table>
  </div>
  <div class="sig">
    <div class="line">Approved by</div>
    <div class="line">Received by<br/><span style="font-size: 10px; color: #555;">${data.employee.name}</span></div>
  </div>
  <div class="foot">Generated by Matcha Story Payroll · ${new Date().toLocaleString("en-MY")}</div>
</body>
</html>`;
}


const PAYMENT_METHODS = [
  { v: "bank_transfer", label: "Bank Transfer" },
  { v: "duitnow", label: "DuitNow" },
  { v: "cash", label: "Cash" },
  { v: "cheque", label: "Cheque" },
  { v: "other", label: "Other" },
];

function BulkMarkPaidModal({ rows, month, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [amounts, setAmounts] = useState(() => {
    const m = {};
    rows.forEach((r) => {
      const amt = Number(r.computedAmount != null ? r.computedAmount : r.estimated_pay || 0);
      m[r.user_id] = String(amt.toFixed(2));
    });
    return m;
  });
  // EPF opt-in per staff — defaults to on for full-time, off for part-time.
  const [epfOn, setEpfOn] = useState(() => {
    const m = {};
    rows.forEach((r) => { m[r.user_id] = r.employment_type === "full_time"; });
    return m;
  });
  const [common, setCommon] = useState({
    paid_date: today,
    method: "bank_transfer",
    reference: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const EPF_RATE = 0.11;
  const grossFor = (r) => Number(amounts[r.user_id] || 0);
  const epfFor = (r) => (epfOn[r.user_id] ? Math.round(grossFor(r) * EPF_RATE * 100) / 100 : 0);
  const netFor = (r) => Math.round((grossFor(r) - epfFor(r)) * 100) / 100;

  const totalGross = rows.reduce((s, r) => s + grossFor(r), 0);
  const totalEpf = rows.reduce((s, r) => s + epfFor(r), 0);
  const totalNet = rows.reduce((s, r) => s + netFor(r), 0);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const items = rows.map((r) => {
        const gross = grossFor(r);
        const epf = epfFor(r);
        const net = netFor(r);
        const noteParts = [];
        if (epf > 0) noteParts.push(`Gross RM ${gross.toFixed(2)} · EPF 11% -RM ${epf.toFixed(2)}`);
        if (common.notes) noteParts.push(common.notes);
        return {
          user_id: r.user_id,
          amount: net, // net is the actual bank-transfer amount
          attendance_ids: r.attendanceIds || null,
          notes: noteParts.join(" · "),
        };
      });
      const { data } = await api.post("/payroll/payments/bulk", {
        month,
        paid_date: common.paid_date,
        method: common.method,
        reference: common.reference,
        notes: common.notes,
        items,
      });
      if (data.skipped && data.skipped.length > 0) {
        setResult(data);
      } else {
        onSaved();
      }
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="payroll-bulk-result-modal">
        <div className="bg-bone w-full max-w-md rounded-sm p-8">
          <h3 className="font-display text-xl text-ink mb-4" style={{ letterSpacing: "0.05em" }}>Payment Recorded</h3>
          <p className="text-sm text-ink-soft mb-4">
            <span className="text-matcha-dark">{result.created.length}</span> payment{result.created.length === 1 ? "" : "s"} recorded
            {result.skipped.length > 0 && <>, <span className="text-red-700">{result.skipped.length}</span> skipped</>}.
          </p>
          {result.skipped.length > 0 && (
            <ul className="text-xs text-ink-soft space-y-1 mb-4 bg-red-50 px-3 py-2 rounded-sm">
              {result.skipped.map((s, i) => <li key={i}>{s.user_id.slice(0, 8)}… · {s.reason}</li>)}
            </ul>
          )}
          <div className="flex justify-end">
            <button onClick={onSaved} className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="payroll-bulk-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-2xl rounded-sm p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>Mark Selected as Paid</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <p className="text-sm text-ink-soft mb-6">Payroll month <span className="text-ink">{month}</span> · {rows.length} staff</p>

        <div className="bg-cream/40 border border-ink/10 rounded-sm overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft bg-sage/30">
                <th className="px-3 py-2 overline">Staff</th>
                <th className="px-3 py-2 overline">Days · Hours</th>
                <th className="px-3 py-2 overline text-right">Gross (RM)</th>
                <th className="px-3 py-2 overline text-right whitespace-nowrap">EPF 11%</th>
                <th className="px-3 py-2 overline text-right">Net (RM)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t border-ink/5">
                  <td className="px-3 py-2 text-ink">
                    {r.full_name}
                    {r.attendanceIds && r.attendanceIds.length < (r.attendance || []).length && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-matcha-dark">partial</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-soft text-xs">
                    {r.computedDays != null ? r.computedDays : r.days_worked} d · {r.computedHours != null ? r.computedHours : r.total_hours} h
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      data-testid={`payroll-bulk-amount-${r.user_id}`}
                      type="number"
                      step="0.01"
                      required
                      className={inputCls + " w-28 text-right"}
                      value={amounts[r.user_id]}
                      onChange={(e) => setAmounts((s) => ({ ...s, [r.user_id]: e.target.value }))}
                    />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`payroll-bulk-epf-${r.user_id}`}
                        checked={!!epfOn[r.user_id]}
                        onChange={(e) => setEpfOn((s) => ({ ...s, [r.user_id]: e.target.checked }))}
                        className="w-3.5 h-3.5 accent-matcha-dark"
                      />
                      <span className={`font-mono text-xs ${epfOn[r.user_id] ? "text-red-700" : "text-ink-soft/50"}`} data-testid={`payroll-bulk-epf-amount-${r.user_id}`}>
                        {epfOn[r.user_id] ? `−${epfFor(r).toFixed(2)}` : "—"}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-ink" data-testid={`payroll-bulk-net-${r.user_id}`}>
                    {netFor(r).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-ink/15 bg-cream/60">
                <td className="px-3 py-3 overline text-ink" colSpan={2}>Totals</td>
                <td className="px-3 py-3 text-right text-sm text-ink" data-testid="payroll-bulk-total-gross">RM {totalGross.toFixed(2)}</td>
                <td className="px-3 py-3 text-right text-sm text-red-700" data-testid="payroll-bulk-total-epf">−RM {totalEpf.toFixed(2)}</td>
                <td className="px-3 py-3 text-right font-display text-lg text-matcha-dark" data-testid="payroll-bulk-total">RM {totalNet.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="bg-sage/20 border border-matcha-dark/20 rounded-sm px-4 py-3 mb-6 text-xs text-ink" data-testid="payroll-bulk-epf-summary">
          <p className="overline text-matcha-dark mb-1">EPF Deduction · 11% (Employee share)</p>
          <p className="text-ink-soft">
            Gross <span className="text-ink font-mono">RM {totalGross.toFixed(2)}</span>
            <span className="mx-2 opacity-40">·</span>
            EPF <span className="text-red-700 font-mono">−RM {totalEpf.toFixed(2)}</span>
            <span className="mx-2 opacity-40">·</span>
            Net Payable <span className="text-matcha-dark font-mono font-semibold">RM {totalNet.toFixed(2)}</span>
          </p>
          <p className="text-[10px] text-ink-soft/70 mt-1">Uncheck the EPF box per staff to skip (e.g. part-time / non-contributing).</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="overline text-ink-soft block mb-2">Paid On</label>
            <input data-testid="payroll-bulk-date" required type="date" className={inputCls + " w-full"} value={common.paid_date} onChange={(e) => setCommon((s) => ({ ...s, paid_date: e.target.value }))} />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Method</label>
            <select data-testid="payroll-bulk-method" className={inputCls + " w-full"} value={common.method} onChange={(e) => setCommon((s) => ({ ...s, method: e.target.value }))}>
              {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="overline text-ink-soft block mb-2">Reference (optional)</label>
            <input data-testid="payroll-bulk-reference" className={inputCls + " w-full"} placeholder="Transaction batch ID, cheque no…" value={common.reference} onChange={(e) => setCommon((s) => ({ ...s, reference: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="overline text-ink-soft block mb-2">Notes (optional)</label>
            <input data-testid="payroll-bulk-notes" className={inputCls + " w-full"} value={common.notes} onChange={(e) => setCommon((s) => ({ ...s, notes: e.target.value }))} />
          </div>
        </div>

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button data-testid="payroll-bulk-confirm" disabled={busy || totalNet <= 0} className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
            {busy ? "Saving…" : `Confirm · RM ${totalNet.toFixed(2)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentDetailsModal({ row, onClose, onDeleted }) {
  const p = row.payment || {};
  const methodLabel = PAYMENT_METHODS.find((m) => m.v === p.method)?.label || p.method || "—";
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(`Remove the payment record for ${row.full_name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/payroll/payments/${p.id}`);
      onDeleted();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="payroll-payment-details-modal">
      <div className="bg-bone w-full max-w-md rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>Payment Record</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <ul className="space-y-3 text-sm">
          {[
            ["Staff", row.full_name],
            ["Month", p.month],
            ["Amount", `RM ${Number(p.amount || 0).toFixed(2)}`],
            ["Paid On", p.paid_date],
            ["Method", methodLabel],
            ["Reference", p.reference || "—"],
            ["Notes", p.notes || "—"],
            ["Recorded By", p.paid_by_name || "—"],
          ].map(([k, v]) => (
            <li key={k} className="flex justify-between border-b border-ink/5 pb-2">
              <span className="text-ink-soft">{k}</span>
              <span className="text-ink text-right">{v}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between gap-3 mt-8">
          <button
            data-testid="payroll-payment-delete"
            onClick={remove}
            disabled={busy}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-red-700 hover:text-red-800 disabled:opacity-50"
          >
            <Trash2 size={14} /> Remove
          </button>
          <button onClick={onClose} className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


function HolidaysTab() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: `${new Date().getFullYear()}-01-01`, name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/public-holidays?year=${year}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const add = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/public-holidays", { date: form.date, name: form.name });
      setForm({ date: `${year}-01-01`, name: "" });
      load();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this public holiday?")) return;
    await api.delete(`/public-holidays/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <label className="overline text-ink-soft">Year</label>
        <input
          data-testid="ph-year"
          type="number"
          min="2020"
          max="2100"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className={inputCls + " w-28"}
        />
        <p className="ml-auto text-sm text-ink-soft">
          {rows.length} holiday{rows.length === 1 ? "" : "s"} in {year}
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5">
          <form onSubmit={add} className="bg-bone border border-ink/10 rounded-sm p-6 space-y-4" data-testid="ph-add-form">
            <h3 className="font-display text-lg text-ink" style={{ letterSpacing: "0.04em" }}>Add Public Holiday</h3>
            <div>
              <label className="overline text-ink-soft block mb-2">Date</label>
              <input data-testid="ph-date" required type="date" className={inputCls + " w-full"} value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-2">Holiday Name</label>
              <input data-testid="ph-name" required className={inputCls + " w-full"} placeholder="Hari Raya, Merdeka Day…" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
            </div>
            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
            <button data-testid="ph-add" disabled={busy} className="w-full bg-ink text-bone rounded-full py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Add Holiday"}
            </button>
            <p className="text-xs text-ink-soft">
              Full-time staff working on these dates earn <span className="text-matcha-dark">2× daily rate</span> automatically.
            </p>
          </form>
        </div>
        <div className="md:col-span-7 bg-bone border border-ink/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sage/40">
              <tr className="text-left text-ink-soft">
                <th className="px-4 py-3 overline">Date</th>
                <th className="px-4 py-3 overline">Name</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-ink/5" data-testid={`ph-row-${h.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{h.date}</td>
                  <td className="px-4 py-3">{h.name}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`ph-del-${h.id}`} onClick={() => del(h.id)} className="text-ink-soft hover:text-red-600">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} className="p-8 text-center text-ink-soft">No public holidays for {year}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
